/**
 * Turning a failure into words the person in front of it can act on.
 *
 * Shared by every admin screen so they all prefer the server's own error code over a
 * generic message. The codes have words in both languages and a test asserts it, so
 * translating the code is strictly better than any sentence written here.
 */

import { ApiError, errorKey } from '@taxi/api-client';

// From its module rather than the barrel: `tests/js` typechecks this file without the DOM
// library, and the barrel brings every primitive with it.
import type { FieldProblem } from '../ui/fieldProblem';

export function describe(
  failure: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  fallback: string,
): string {
  return failure instanceof ApiError
    ? t(errorKey(failure.problem), { ...failure.problem.arguments, defaultValue: t(fallback) })
    : t(fallback);
}

/**
 * The words, and which field they are about.
 *
 * A validation refusal names its field — `AppError.Validation` puts it in the problem's
 * arguments — and a page that hands this to `FieldErrors` gets that field marked where it
 * sits, rather than a sentence at the top of a page of twenty settings that does not say
 * which one it means.
 */
export function refusal(
  failure: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  fallback: string,
): FieldProblem {
  const field =
    failure instanceof ApiError && failure.problem.code.startsWith('validation.')
      ? (failure.problem.arguments?.field ?? null)
      : null;

  return { field, message: describe(failure, t, fallback) };
}

/**
 * What the browser found wrong with one control, in the terms its constraint validation
 * uses. Structural, so it can be built in a test without a DOM.
 */
export interface Invalidity {
  /** The control's id, which is the API's name for the field. */
  field: string;
  /** The words of its label, so the banner can say which field it means. */
  label: string;
  /** `type="number"`: an empty box is "type a number", not merely "required". */
  numeric: boolean;
  valueMissing: boolean;
  badInput: boolean;
  rangeUnderflow: boolean;
  rangeOverflow: boolean;
  /** More decimals than the box's `step` allows: 1.005 in a box counted in cents. */
  stepMismatch: boolean;
  min: string;
  max: string;
}

/** The sentence under one field: what is wrong, said so the person knows what to type. */
export function reasonFor(
  invalid: Invalidity,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  // With the box's own floor when it has one: "0 if there is none" is right for a fee and
  // wrong for an offer window that starts at five seconds.
  if ((invalid.valueMissing || invalid.badInput) && invalid.numeric) {
    return invalid.min === ''
      ? t('form.enterANumber')
      : t('form.enterANumberFrom', { min: invalid.min });
  }

  if (invalid.valueMissing) return t('errors.validation.required');
  if (invalid.rangeUnderflow) return t('form.atLeast', { min: invalid.min });
  if (invalid.rangeOverflow) return t('form.atMost', { max: invalid.max });
  if (invalid.stepMismatch) return t('form.tooPrecise');

  return t('errors.validation.invalid_format');
}

/**
 * Every field a form would be refused over, as one problem — or null when there is none.
 *
 * Checked before the request rather than left to the server, because the server can name one
 * field per answer and an empty number box never reaches its checks at all: it arrives as
 * null and the body is refused unread. Each field gets its own reason under it, and the
 * banner names them, so somebody looking at the top of a long form knows where to go (#377).
 */
export function problemOf(
  found: readonly Invalidity[],
  t: (key: string, options?: Record<string, unknown>) => string,
): FieldProblem | null {
  const first = found[0];

  if (first === undefined) return null;

  const fields = found.map((invalid) => ({ field: invalid.field, message: reasonFor(invalid, t) }));

  return {
    field: first.field,
    message:
      found.length === 1
        ? t('form.oneField', { label: first.label, message: fields[0]!.message })
        : t('form.severalFields', {
            count: found.length,
            labels: found.map((invalid) => invalid.label).join(', '),
          }),
    fields,
  };
}
